import LegalShell from "./LegalShell";

export default function SvaDetoxPrivacy() {
  return (
    <LegalShell title="Privacy Policy" product="sva-detox">
      <p>
        This Privacy Policy describes how <strong>BastionGuard Medical Technologies Pvt. Ltd.</strong> ({'"'}we{'"'}, {'"'}us{'"'}, {'"'}our{'"'}) collects, uses, stores and discloses personal information when you use the <strong>Sva — Detox</strong> mobile application (the {'"'}App{'"'}) and related services.
      </p>
      <p>
        We are committed to handling your data in accordance with the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong>, the <strong>Information Technology Act, 2000</strong> and its Rules, and applicable laws of India.
      </p>

      <h2>1. Who we are</h2>
      <p><strong>Data Fiduciary:</strong> BastionGuard Medical Technologies Pvt. Ltd.</p>
      <p><strong>Registered Office:</strong> 372/E 1, Karunya Building, Podiyadi, Pathanamthitta – 689110, Kerala, India</p>
      <p><strong>GSTIN:</strong> 32AAOCB1809M1ZK</p>
      <p><strong>Grievance Officer / Contact:</strong> <a href="mailto:info@bastionguardhealthcare.in">info@bastionguardhealthcare.in</a></p>

      <h2>2. Information we collect</h2>

      <h3>2.1 Information you give us</h3>
      <ul>
        <li><strong>Account data</strong> — name, email address, password (stored only as a one-way bcrypt hash), optional Google account profile picture and name (if you sign in with Google).</li>
        <li><strong>Recovery email</strong> — a separate email you provide to recover your account.</li>
        <li><strong>Role</strong> — Parent or Child / Self.</li>
        <li><strong>Privileged (Parent) Password</strong> and <strong>daily Passcode</strong> — stored only as bcrypt hashes.</li>
        <li><strong>Onboarding answers</strong> — age range, audience, current daily screen-time estimate, top distractions, motivation, friction style preferences, daily goal.</li>
        <li><strong>App-usage settings you configure</strong> — list of tracked apps, detox windows, habits, daily intention text, mood selections.</li>
        <li><strong>AI conversations</strong> — messages you exchange with our in-app coach {'"'}Sva{'"'}.</li>
        <li><strong>Billing information you provide at checkout</strong> — billing state, optional GSTIN (for tax-invoice purposes only), optional phone number for UPI AutoPay.</li>
      </ul>

      <h3>2.2 Information collected automatically</h3>
      <ul>
        <li><strong>Activity events</strong> — focus sessions, multi-step challenges (success/failure), tap counts on tracked apps, achievements earned.</li>
        <li><strong>Device data</strong> — operating system version, device model (collected only in aggregate, no advertising identifiers).</li>
        <li><strong>Crash and error logs</strong> — used only to fix the app; do not contain content of your AI conversations or your passwords.</li>
      </ul>

      <h3>2.3 Information collected via device permissions (Android-only, opt-in)</h3>
      <p>If and when you grant the relevant Android permissions:</p>
      <ul>
        <li><strong>Usage statistics</strong> (<code>PACKAGE_USAGE_STATS</code>) — aggregate per-app foreground minutes, used to compute your daily screen-time and per-app statistics on-device.</li>
        <li><strong>Accessibility service</strong> — used solely to detect when a tracked app is opened so the App can launch the in-app challenge. We do <strong>not</strong> read screen contents, capture passwords or harvest data from other apps.</li>
        <li><strong>Display over other apps</strong> — used to render the challenge UI above the target app; no screen capture or recording.</li>
      </ul>
      <p>These permissions can be revoked any time from Android Settings.</p>

      <h3>2.4 Information we do NOT collect</h3>
      <ul>
        <li>Contact lists, SMS messages, call logs, photos, files, location.</li>
        <li>Advertising identifiers — we do not run ads.</li>
        <li>Biometric data.</li>
        <li>Card or bank account numbers — all payments are processed by Razorpay; we never see the full card number or CVV.</li>
      </ul>

      <h2>3. Why we use your information (purposes)</h2>
      <ol>
        <li>To create and operate your account and provide the App{"'"}s features.</li>
        <li>To personalise your experience using your onboarding answers.</li>
        <li>To generate AI coach responses by sending your message and limited profile context (name, goal, today{"'"}s screen-time, tracked apps) to our AI model provider (Anthropic Claude via Emergent integrations).</li>
        <li>To process subscription payments and issue GST-compliant tax invoices.</li>
        <li>To prevent abuse, secure accounts and respond to user-support requests.</li>
        <li>To comply with our legal, regulatory and tax obligations.</li>
      </ol>

      <h2>4. Legal basis (DPDP Act, 2023)</h2>
      <p>We process personal data on the following legal bases:</p>
      <ul>
        <li><strong>Consent</strong> — given explicitly during signup and onboarding.</li>
        <li><strong>Legitimate use</strong> — for fraud prevention, security, and compliance with court orders or law.</li>
        <li><strong>Contractual necessity</strong> — to deliver the subscription you purchased.</li>
      </ul>

      <h2>5. Third parties who process data on our behalf</h2>
      <p>We share <strong>only the minimum data required</strong> with the following {'"'}Data Processors{'"'}, each of whom is bound by appropriate contractual terms:</p>
      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr><th>Processor</th><th>Purpose</th><th>Data shared</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Razorpay</strong></td><td>Payment processing & subscription billing</td><td>Name, email, phone, billing state, optional GSTIN</td></tr>
            <tr><td><strong>Maileroo</strong></td><td>Transactional email delivery</td><td>Email address, name, invoice PDF</td></tr>
            <tr><td><strong>Anthropic</strong> (via Emergent)</td><td>AI coach replies</td><td>Your message text + limited profile context</td></tr>
            <tr><td><strong>Google</strong> (optional)</td><td>Authentication</td><td>Google account name, email, profile picture URL</td></tr>
            <tr><td><strong>Cloud hosting</strong></td><td>App backend hosting in India / SE-Asia</td><td>All of the above, encrypted in transit and at rest</td></tr>
          </tbody>
        </table>
      </div>
      <p>We do <strong>not</strong> sell your personal data and we do not share it for third-party advertising.</p>

      <h2>6. Data retention</h2>
      <ul>
        <li>Active-account data: retained while your account exists.</li>
        <li>AI conversation history: retained until you delete it or your account.</li>
        <li>Tax invoices & payment records: retained for <strong>8 years</strong> as required by Indian tax law.</li>
        <li>Crash / error logs: 90 days.</li>
        <li>On account deletion: we erase all personal data within <strong>30 days</strong>, except where retention is required by law.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>Under the DPDP Act you have the right to:</p>
      <ul>
        <li><strong>Access</strong> the data we hold about you.</li>
        <li><strong>Correct or update</strong> any inaccurate information.</li>
        <li><strong>Erase</strong> your data (delete your account from Profile → Sign out → {'"'}Delete account{'"'}, or by emailing us).</li>
        <li><strong>Withdraw consent</strong> — by uninstalling the App and deleting your account.</li>
        <li><strong>Nominate</strong> another person to exercise these rights in the event of your death or incapacity.</li>
        <li><strong>Grievance redressal</strong> — write to our Grievance Officer; we will acknowledge within 24 hours and respond within <strong>15 days</strong>.</li>
      </ul>

      <h2>8. Children{"'"}s privacy</h2>
      <p>The App is intended for users <strong>18 years and older</strong>, and for parents to manage the phone usage of their children. If a parent registers a child under 18:</p>
      <ul>
        <li>The parent is the verifiable consenting adult and assumes full responsibility for the data of the child.</li>
        <li>We do not knowingly process personal data of any child without parental consent.</li>
        <li>No advertising of any kind is shown.</li>
        <li>The AI coach does not collect or store the child{"'"}s identifying information beyond what is required for the App to function.</li>
      </ul>
      <p>If you believe a child has provided personal data to us without parental consent, email <a href="mailto:info@bastionguardhealthcare.in">info@bastionguardhealthcare.in</a> and we will delete it.</p>

      <h2>9. Security</h2>
      <p>We use industry-standard safeguards including TLS 1.2+ encryption in transit, encryption at rest, bcrypt password hashing, HMAC-verified webhooks, and role-based access controls. While no system is perfectly secure, we promptly investigate and notify affected users of any breach in accordance with the IT Rules and DPDP Act.</p>

      <h2>10. International data transfers</h2>
      <p>Most of our processors store data in India or the European Union. Some (Anthropic, Maileroo) are based outside India; transfers to such jurisdictions are made under appropriate contractual safeguards.</p>

      <h2>11. Cookies and analytics</h2>
      <p>The mobile App does not use cookies. We do not currently run any third-party analytics SDKs.</p>

      <h2>12. Changes to this policy</h2>
      <p>We may update this policy from time to time. Material changes will be notified within the App and via email at least 7 days before they take effect.</p>

      <h2>13. Contact us</h2>
      <p><strong>Grievance Officer:</strong></p>
      <p>BastionGuard Medical Technologies Pvt. Ltd.<br />372/E 1, Karunya Building, Podiyadi, Pathanamthitta – 689110, Kerala, India<br />Email: <a href="mailto:info@bastionguardhealthcare.in">info@bastionguardhealthcare.in</a></p>
    </LegalShell>
  );
}
